import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { fmtNum, statusClass } from "../lib/format";

export default function Support() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet("/support/summary").then(setD); }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;
  const postureTone = d.posture_score >= 85 ? "var(--cv-green)"
    : d.posture_score >= 70 ? "#D55816" : "var(--cv-crimson)";

  return (
    <>
      <Masthead
        module="support"
        eyebrow="Support Effectiveness · IT Service Desk + Vendor / OEM"
        title="Support Effectiveness"
        subtitle="Ticket volume · MTTR · CSAT · SLA attainment · vendor SLA tracking"
        meta={<div>{fmtNum(k.open_tickets)} open · {fmtNum(k.closed_30d)} closed (30d)<br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <button type="button" data-testid="sup-posture"
                  onClick={() => nav("/drill/support_tickets")}
                  className="cv-panel text-left hover:shadow-lg transition-shadow"
                  style={{ borderLeft: `4px solid ${postureTone}`, cursor: "pointer" }}>
            <div className="cv-kpi-label">Support Posture</div>
            <div className="flex items-end gap-3 mt-3">
              <div className="font-serif-display leading-none" style={{ color: postureTone, fontSize: 60 }}>
                {d.posture_score}
              </div>
              <div className="pb-2">
                <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>/ 100</div>
                <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>target {d.posture_target}</div>
              </div>
            </div>
            <div className="cv-kpi-sub mt-2">
              Composite of CSAT, SLA attainment, MTTR vs target &amp; escalation rate.
            </div>
          </button>
          <Kpi testid="sup-csat" label="CSAT" value={`${k.csat_pct}%`}
               sub={`target ${k.csat_target}% · rolling 30d`}
               alert={k.csat_pct < k.csat_target}
               onClick={() => nav("/drill/support_tickets")} />
          <Kpi testid="sup-mttr" label="Mean time to resolve" value={`${k.mttr_hrs} hrs`}
               sub={`target ${k.mttr_target_hrs} hrs`}
               alert={k.mttr_hrs > k.mttr_target_hrs}
               onClick={() => nav("/drill/support_tickets?state=Open")} />
          <Kpi testid="sup-sla" label="SLA attainment" value={`${k.sla_attainment_pct}%`}
               sub={`target ${k.sla_target_pct}% · ${k.escalations_30d} escalations (30d)`}
               alert={k.sla_attainment_pct < k.sla_target_pct}
               onClick={() => nav("/drill/support_tickets")} />
        </div>

        {/* Ticket priority + category */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-tickets-priority" title="Tickets by Priority" subtitle="Open · closed 30d · MTTR · SLA %">
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">Priority</th>
                    <th className="py-2 px-2 font-bold text-right">Open</th>
                    <th className="py-2 px-2 font-bold text-right">Closed 30d</th>
                    <th className="py-2 px-2 font-bold text-right">MTTR (hrs)</th>
                    <th className="py-2 px-2 font-bold text-right">SLA %</th>
                  </tr>
                </thead>
                <tbody>
                  {d.tickets_by_priority.map((p) => (
                    <tr key={p.priority} data-testid={`prio-row-${p.priority.split(' ')[0].toLowerCase()}`}
                        onClick={() => nav(`/drill/support_tickets?priority=${encodeURIComponent(p.priority)}`)}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--cv-line)" }}>
                      <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-charcoal)" }}>{p.priority}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(p.open)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(p.closed_30d)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{p.mttr_hrs}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-bold" style={{ color: p.sla_pct >= 95 ? "var(--cv-green)" : (p.sla_pct >= 85 ? "#D55816" : "var(--cv-crimson)") }}>{p.sla_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testid="panel-tickets-category" title="Tickets by Category" subtitle="Where volume is concentrated">
            <div className="flex flex-col gap-2">
              {d.tickets_by_category.map((c) => {
                const max = Math.max(...d.tickets_by_category.map(x => x.open));
                const pct = Math.round((c.open / max) * 100);
                return (
                  <button type="button" key={c.key}
                          data-testid={`tk-cat-${c.key}`}
                          onClick={() => nav(`/drill/support_tickets?category_key=${c.key}`)}
                          className="text-left w-full py-1 hover:opacity-80 transition-opacity"
                          style={{ background: "transparent", border: 0, cursor: "pointer" }}>
                    <div className="flex justify-between items-center text-[13px] mb-0.5">
                      <span className="truncate pr-2" style={{ color: "var(--cv-charcoal)" }}>{c.category}</span>
                      <span className="tabular-nums font-bold" style={{ color: "var(--cv-charcoal)" }}>
                        {fmtNum(c.open)} <span className="text-[11px] font-normal" style={{ color: "var(--cv-charcoal-soft)" }}>({c.share_pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--cv-crimson), #D55816)" }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>
        </div>

        {/* Vendors + Top analysts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-vendors" title="Vendor / OEM Support Performance"
                 subtitle="CrowdStrike · Zscaler · Fortinet · Microsoft · CyberArk · Qualys · Mimecast">
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">Vendor</th>
                    <th className="py-2 px-2 font-bold text-right">Tickets 30d</th>
                    <th className="py-2 px-2 font-bold text-right">Avg resp (hrs)</th>
                    <th className="py-2 px-2 font-bold text-right">SLA %</th>
                    <th className="py-2 px-2 font-bold text-right">CSAT</th>
                    <th className="py-2 px-2 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.vendors.map((v) => (
                    <tr key={v.key} data-testid={`vendor-${v.key}`}
                        onClick={() => nav(`/drill/vendor_tickets?vendor_key=${v.key}`)}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--cv-line)" }}>
                      <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-charcoal)" }}>{v.vendor}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(v.tickets_30d)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{v.avg_response_hrs}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-bold" style={{ color: v.sla_pct >= 95 ? "var(--cv-green)" : (v.sla_pct >= 88 ? "#D55816" : "var(--cv-crimson)") }}>{v.sla_pct}%</td>
                      <td className="py-2 px-2 text-right tabular-nums">{v.csat}</td>
                      <td className="py-2 px-2"><span className={`status-pill ${statusClass(v.status)}`}>{v.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testid="panel-analysts" title="Top Analysts" subtitle="Closed 30d · CSAT · MTTR">
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">Analyst</th>
                    <th className="py-2 px-2 font-bold">Role</th>
                    <th className="py-2 px-2 font-bold text-right">Closed 30d</th>
                    <th className="py-2 px-2 font-bold text-right">CSAT</th>
                    <th className="py-2 px-2 font-bold text-right">MTTR (hrs)</th>
                  </tr>
                </thead>
                <tbody>
                  {d.top_analysts.map((a) => (
                    <tr key={a.name}
                        onClick={() => nav(`/drill/support_tickets?assignee=${encodeURIComponent(a.name)}`)}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--cv-line)" }}>
                      <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-charcoal)" }}>{a.name}</td>
                      <td className="py-2 px-2" style={{ color: "var(--cv-charcoal-soft)" }}>{a.role}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(a.closed_30d)}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-bold" style={{ color: a.csat >= 90 ? "var(--cv-green)" : (a.csat >= 85 ? "#D55816" : "var(--cv-crimson)") }}>{a.csat}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{a.mttr_hrs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
